/**
 * NFC Solutions — Local Event Engine (write-side).
 *
 * ARCHITECTURAL CONTRACT
 * ──────────────────────
 * From Session 26 (Milestone B) onwards, every restaurant mutation
 * follows this exact shape:
 *
 *   local_write(event)  →  persist to IDB event_queue    (LOCAL)
 *                       →  reducer applies to IDB stores  (LOCAL)
 *                       →  append to event_log            (LOCAL)
 *                       →  dispatch UI refresh event      (LOCAL)
 *                       →  return { change_id, applied }  ← UI proceeds
 *
 * *Then*, asynchronously and NEVER on the UI critical path:
 *
 *   LocalCoordinator.broadcast(event)  →  peer devices apply the
 *                                          same reducer to their IDB
 *   eventSync loop drains queue        →  POST /api/events/sync
 *                                          → mark SYNCED on 200
 *                                          → stays PENDING otherwise
 *
 * THE FIVE RULES
 * ──────────────
 *   1. `local_write` is synchronous from the caller's perspective.
 *      It MUST return before any network activity happens.
 *   2. Every read comes from the local database. UI never awaits a
 *      cloud round-trip to render.
 *   3. `change_id` (client-generated uuid) is the identity of the
 *      change — the cloud dedupes on this, LAN peers dedupe on this.
 *   4. Reducers must be pure and idempotent: applying the same event
 *      twice must produce the same IDB state as applying it once.
 *   5. The cloud is a synchronisation / backup / admin tool. It is
 *      never the primary source of truth during restaurant operation.
 *
 * This module is intentionally free of any React or Capacitor code so
 * it can be exercised from tests and from a headless coordinator.
 */

import {
  createChangeEnvelope,
  EventLifecycle,
  EventType,
} from "./events";
import {
  putOne,
  deleteOne,
  readAll,
  readOne,
  appendEventLog,
} from "./localDb";
import { getCoordinator } from "./coordinator";

// DOM CustomEvent that any UI subscriber (Staff dashboard, Kitchen,
// Owner, Bar) can listen to for optimistic UI refresh. We deliberately
// piggyback on the same `nfcs:` event namespace used by the WebSocket
// realtime layer so consumers can pick up local + remote changes with
// one listener.
export const LOCAL_EVENT_APPLIED = "nfcs:local-event-applied";

// ─────────────────────────────────────────────────────────────────
// Reducer registry
// ─────────────────────────────────────────────────────────────────
// A reducer for event type `T` takes the event payload and mutates
// the local IndexedDB stores to reflect the change. It must NOT make
// network calls. It must be idempotent (safe to run twice with the
// same change_id).
//
// New event types add a reducer here; unknown types are logged but do
// not break the write path (forward-compat).

const REDUCERS = new Map();

export function registerReducer(eventType, fn) {
  if (typeof fn !== "function") return;
  REDUCERS.set(String(eventType), fn);
}

export function getReducer(eventType) {
  return REDUCERS.get(String(eventType)) || null;
}

// ─────────────────────────────────────────────────────────────────
// Core: local_write(event) — the single write primitive.
// ─────────────────────────────────────────────────────────────────

/**
 * Persist + apply + broadcast an event.
 *
 *   const { change_id, applied } = await local_write({
 *     type: EventType.ORDER_CREATED,
 *     payload: { ... },
 *     session,   // current user session for envelope metadata
 *   });
 *
 * Returns as soon as the local database has been updated — no cloud
 * round-trip. The cloud sync loop (see `./eventSync.js`) drains the
 * queue in the background.
 *
 * Errors from the reducer PROPAGATE (so the UI can show a toast) but
 * the event is still persisted to `event_queue` with `_meta.status
 * = FAILED_LOCAL` so operators can inspect what went wrong in the
 * Developer Tools event log.
 */
export async function localWrite({ type, payload, session, parentChangeId, changeId } = {}) {
  if (!type) throw new Error("local_write: event type is required");

  // 1. Envelope. Client-generated change_id — this is the identity.
  //    Session 40 — callers that already tried the same action over HTTP
  //    pass the change_id they used, so the server recognises the replay.
  const envelope = createChangeEnvelope(type, payload || {}, { session: session || null });
  if (parentChangeId) envelope.parent_change_id = parentChangeId;
  if (changeId) envelope.change_id = String(changeId);

  // 2. Queue row — starts in LOCAL. The sync loop transitions it to
  //    PENDING → SYNCED (or FAILED). Every field the loop needs is
  //    stashed under `_meta`.
  const row = {
    ...envelope,
    id: envelope.change_id,
    _meta: {
      ...envelope._meta,
      status: EventLifecycle.LOCAL,
      queued_at: envelope.ts,
    },
  };

  // 3. Persist to event_queue BEFORE applying the reducer. If the
  //    process crashes between here and the reducer, the sync loop
  //    still eventually replays this event when the app relaunches.
  await putOne("event_queue", row);

  // 4. Reducer — pure IDB mutation. Any exception is captured but
  //    we still return so the UI can fall back to a re-hydration
  //    from the cloud.
  let reducerError = null;
  const reducer = getReducer(type);
  if (reducer) {
    try {
      await reducer(envelope.payload, envelope);
    } catch (e) {
      reducerError = e;
      try {
        await putOne("event_queue", {
          ...row,
          _meta: {
            ...row._meta,
            status: EventLifecycle.LOCAL,
            last_error: String((e && e.message) || e),
          },
        });
      } catch (_) { /* noop */ }
    }
  }

  // 5. Ring-buffered log for diagnostics.
  try { await appendEventLog(envelope); } catch (e) { console.warn("[eventEngine] appendEventLog error:", e?.message || e); }

  // 6. Notify local subscribers so open dashboards refresh their
  //    in-memory copies without waiting for the cloud WS to echo
  //    the event back.
  try {
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      window.dispatchEvent(new CustomEvent(LOCAL_EVENT_APPLIED, { detail: envelope }));
    }
  } catch (_) { /* noop */ }

  // 7. Route through the Coordinator (Phase 2, Session 27.55+).
  //
  // If today's Coordinator is a DIFFERENT device on our LAN, hand
  // the envelope off to it via `coordinatorMesh.routeWriteThroughCoordinator`
  // — it takes care of the WebRTC send + ACK. When the Coordinator
  // receives it, it persists to its OWN event_queue and lets its
  // sync loop push to /api/events/sync (single-writer semantics =
  // no double orders even on retry storms).
  //
  // If we ARE the Coordinator, if there's no Coordinator elected
  // today, or if the mesh is offline, this function returns
  // route: "self" or route: "cloud" respectively and the classic
  // cloud sync loop below picks up the queued row on its next tick.
  //
  // We do NOT `await` this — the mesh send has its own 1s cap and
  // the UI has already been repainted by step 4. Any error is
  // swallowed so a mesh hiccup can never affect the write path.
  try {
    // Dynamic import so this file has no circular dep with
    // coordinatorMesh.js (which imports applyRemoteEvent from us).
    import("./coordinatorMesh")
      .then((m) => m.routeWriteThroughCoordinator(envelope))
      .catch(() => { /* mesh routing is best-effort */ });
  } catch (_) { /* noop */ }

  // 8. Legacy coordinator broadcast — kept as a no-op relay for now
  //    so the old cloud-relay shim still ticks its subscribe list.
  //    When Phase 2 is fully bedded in we'll remove this.
  try {
    const coordinator = getCoordinator();
    if (coordinator && typeof coordinator.broadcast === "function") {
      // Fire and forget — the sync loop is what guarantees delivery.
      Promise.resolve(coordinator.broadcast(envelope)).catch(() => {});
    }
  } catch (_) { /* noop */ }

  return {
    change_id: envelope.change_id,
    applied: !reducerError,
    error: reducerError ? String((reducerError && reducerError.message) || reducerError) : null,
    envelope,
  };
}

// ─────────────────────────────────────────────────────────────────
// Remote event application — used by the sync loop when the cloud
// (or a LAN peer) hands us an event we didn't originate. Same
// reducer, same idempotency guarantee. Never touches event_queue.
// ─────────────────────────────────────────────────────────────────

export async function applyRemoteEvent(envelope) {
  if (!envelope || typeof envelope !== "object") return { applied: false };
  const type = envelope.event_type || envelope.type;
  const reducer = getReducer(type);
  if (!reducer) return { applied: false, reason: "unknown-event-type" };
  try {
    await reducer(envelope.payload || {}, envelope);
    try { await appendEventLog({ ...envelope, _remote: true }); } catch (e) { console.warn("[eventEngine] appendEventLog (remote) error:", e?.message || e); }
    return { applied: true };
  } catch (e) {
    return { applied: false, reason: String((e && e.message) || e) };
  }
}

// ─────────────────────────────────────────────────────────────────
// Queue inspection helpers — used by Developer Tools and by the
// cloud sync loop.
// ─────────────────────────────────────────────────────────────────

export async function listQueuedEvents(filter = {}) {
  const rows = await readAll("event_queue");
  const statusFilter = filter.status ? String(filter.status) : null;
  const out = statusFilter
    ? rows.filter((r) => r && r._meta && r._meta.status === statusFilter)
    : rows;
  out.sort((a, b) => String(a.ts || "").localeCompare(String(b.ts || "")));
  return out;
}

export async function markEventStatus(changeId, patch) {
  if (!changeId || !patch) return false;
  try {
    const row = await readOne("event_queue", changeId);
    if (!row) return false;
    const next = {
      ...row,
      _meta: {
        ...(row._meta || {}),
        ...(patch._meta || {}),
        status: patch.status || row._meta?.status || EventLifecycle.LOCAL,
      },
    };
    await putOne("event_queue", next);
    return true;
  } catch (_) { return false; }
}

export async function discardSyncedEvents({ maxAgeMs = 24 * 60 * 60 * 1000 } = {}) {
  // Housekeeping — remove SYNCED events older than 24h so the queue
  // doesn't grow without bound. The event log retains the last 500
  // for diagnostics either way.
  try {
    const now = Date.now();
    const rows = await readAll("event_queue");
    for (const r of rows) {
      if (!r || !r._meta) continue;
      if (r._meta.status !== EventLifecycle.SYNCED) continue;
      const syncedAt = r._meta.synced_at ? new Date(r._meta.synced_at).getTime() : 0;
      if (!syncedAt || (now - syncedAt) > maxAgeMs) {
        try { await deleteOne("event_queue", r.id); } catch (_) { /* noop */ }
      }
    }
  } catch (_) { /* noop */ }
}

// ─────────────────────────────────────────────────────────────────
// Reducers
// ─────────────────────────────────────────────────────────────────
// Each reducer receives the event payload and the full envelope.
// It must ONLY touch IDB and must be safe to replay.
//
// Reducers are registered as side-effects of importing this module
// so the write path is guaranteed to know about them before the
// first `local_write` call.

registerReducer(EventType.ORDER_CREATED, async (payload, envelope) => {
  // Payload shape for order_created:
  //   {
  //     order: { id, table_session_id, order_number, status, source,
  //              created_at, items: [{ menu_item_id, quantity, ... }],
  //              subtotal, total },
  //     table_session: { id, table_number, status, updated_at }?,
  //   }
  if (!payload || typeof payload !== "object") return;
  const order = payload.order || null;
  const session = payload.table_session || null;
  if (order && order.id) {
    // Idempotent upsert — merges with any existing row so the
    // authoritative cloud row (once the sync ACK arrives) can
    // enrich fields we didn't have locally (e.g. server order_number).
    const existing = (await readOne("orders", order.id)) || {};
    await putOne("orders", {
      ...existing,
      ...order,
      // Retain the change_id for cross-referencing when reconciling
      // with the cloud's echo of the same event.
      _change_id: order._change_id || envelope.change_id,
    });
  }
  if (session && session.id) {
    const existingSession = (await readOne("table_sessions", session.id)) || {};
    await putOne("table_sessions", { ...existingSession, ...session });
  }
});

registerReducer(EventType.ORDER_STATUS_CHANGED, async (payload) => {
  if (!payload || !payload.order_id) return;
  const existing = await readOne("orders", payload.order_id);
  if (!existing) return;
  await putOne("orders", {
    ...existing,
    status: payload.status || existing.status,
    updated_at: payload.ts || existing.updated_at,
  });
});

registerReducer(EventType.TABLE_OPENED, async (payload) => {
  const s = payload && payload.table_session;
  if (!s || !s.id) return;
  const existing = (await readOne("table_sessions", s.id)) || {};
  await putOne("table_sessions", { ...existing, ...s, status: s.status || "open" });
});

registerReducer(EventType.TABLE_CLOSED, async (payload) => {
  const sid = payload && (payload.table_session_id || (payload.table_session && payload.table_session.id));
  if (!sid) return;
  const existing = await readOne("table_sessions", sid);
  if (!existing) return;
  await putOne("table_sessions", { ...existing, status: "closed", closed_at: payload.ts || new Date().toISOString() });
});

// Session 27.37 — PAYMENT_RECORDED reducer for the Local Event Engine.
//
// Before this migration `savePayment` did an `await http.patch(...)` on
// the critical UI path. When Cloudflare had a 5-10s latency spike (a
// regular occurrence on flaky wifi) the "Save Payment" spinner spun
// forever and the waiter couldn't move on. Under the local-first
// contract the reducer applies the payment to the local IDB row
// *immediately* and the sync loop pushes it to Mongo later — matching
// the behaviour of `handleAddItems`, `submitOpenTable`, and `closeTable`.
//
// Payload shape (mirrors PATCH /staff/table-sessions/{id}/payment):
//   {
//     table_session_id: string,      // required
//     cash_paid:      number,
//     card_paid:      number,
//     tip_amount:     number,
//     tip_recipient:  string | null,
//     tip_recipient_user_id: string | null,
//     payment_method: "cash" | "card" | "split" | "unpaid",
//     payment_recorded_at: iso string,
//   }
//
// Idempotent: applying the same event twice produces the same IDB
// state. Server-side dedupe happens via `change_id`.
registerReducer(EventType.PAYMENT_RECORDED, async (payload) => {
  if (!payload || typeof payload !== "object") return;
  const sid = payload.table_session_id;
  if (!sid) return;
  const session = await readOne("table_sessions", sid);
  const now = payload.payment_recorded_at || new Date().toISOString();
  const patch = {
    cash_paid: Number(payload.cash_paid) || 0,
    card_paid: Number(payload.card_paid) || 0,
    eft_paid: Number(payload.eft_paid) || 0,
    tip_amount: Number(payload.tip_amount) || 0,
    tip_recipient: payload.tip_recipient || null,
    tip_recipient_user_id: payload.tip_recipient_user_id || null,
    payment_method: payload.payment_method || "unpaid",
    payment_recorded_at: now,
    updated_at: now,
  };
  // Session 31.26 — mirror the server: the legacy overwrite becomes
  // ledger entries (tip rides on the card, else EFT, else cash) so the
  // receipt's Payments panel shows the same rows the cloud will return.
  {
    const tipOn = patch.card_paid > 0 ? "card" : patch.eft_paid > 0 ? "eft" : "cash";
    const rows = [];
    if (patch.cash_paid > 0) rows.push({ method: "cash", amount: patch.cash_paid, tip: tipOn === "cash" ? patch.tip_amount : 0, label: "Cash" });
    if (patch.card_paid > 0) rows.push({ method: "card", amount: patch.card_paid, tip: tipOn === "card" ? patch.tip_amount : 0, label: "Card 1" });
    if (patch.eft_paid > 0) rows.push({ method: "eft", amount: patch.eft_paid, tip: tipOn === "eft" ? patch.tip_amount : 0, label: "EFT" });
    const keepSplit = ((session && session.payments) || []).filter((p) => p && p.split_id);
    patch.payments = keepSplit.concat(rows.map((r, i) => ({ id: `${now}-${i}`, recorded_at: now, ...r })));
  }
  if (session && session.id) {
    await putOne("table_sessions", { ...session, ...patch });
  } else {
    // No local session yet — stash a stub so the UI can still reflect
    // the payment when the session snapshot arrives. Idempotent: a
    // later authoritative row will overwrite this.
    await putOne("table_sessions", {
      id: sid,
      status: "open",
      ...patch,
    });
  }
  // Mirror onto every order in the session (same behaviour as the
  // legacy PATCH endpoint). We can't easily pick the "biggest order"
  // client-side without loading all order rows — so stamp the payment
  // method + cash/card/paid on ALL orders in the session, and let the
  // server-side reducer's authoritative "biggest order gets the tip"
  // rule apply when the sync ACK returns and refreshes the rows.
  try {
    const orders = await readAll("orders");
    const sessionOrders = orders.filter((o) => o && o.table_session_id === sid);
    for (const o of sessionOrders) {
      await putOne("orders", {
        ...o,
        cash_paid: patch.cash_paid,
        card_paid: patch.card_paid,
        payment_method: patch.payment_method,
        // Don't touch tip fields locally — the server picks a single
        // tip target (biggest order in the session). The WS echo of
        // `payment_recorded` will backfill the correct order.
        updated_at: now,
      });
    }
  } catch (_) { /* best effort */ }
});

// Export the constants callers reach for so they don't need to
// import both `events.js` and this module.
export { EventType, EventLifecycle };

// ─────────────────────────────────────────────────────────────────
// Session 31.26 — Multi-tender ledger helpers (shared by the
// PAYMENT_ADDED reducer below and the dashboards' optimistic patches).
// Mirrors the backend `_ledger_totals` / `_method_from_totals` /
// `_apply_payment_ledger` so a device that adds a tender offline shows
// exactly what the server will compute once the event syncs.
// ─────────────────────────────────────────────────────────────────
const _r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export function ledgerTotals(payments) {
  const out = { cash: 0, card: 0, eft: 0, tip: 0, paid: 0 };
  for (const p of Array.isArray(payments) ? payments : []) {
    const m = String((p && p.method) || "").toLowerCase();
    if (m !== "cash" && m !== "card" && m !== "eft") continue;
    out[m] = _r2(out[m] + (Number(p.amount) || 0));
    out.tip = _r2(out.tip + (Number(p.tip) || 0));
  }
  out.paid = _r2(out.cash + out.card + out.eft);
  return out;
}

export function methodFromTotals(cash, card, eft) {
  const used = [["cash", cash], ["card", card], ["eft", eft]].filter(([, v]) => v > 0).map(([m]) => m);
  if (!used.length) return "unpaid";
  return used.length === 1 ? used[0] : "split";
}

/**
 * Session 34 — service-fee percentage that applies to a session snapshot.
 *   • settled bills use the pct LOCKED on the session (0 for legacy ones)
 *   • open bills follow the server payload, else the live business setting
 */
export function sessionServiceFeePct(session, business) {
  const s = session || {};
  const b = business || {};
  const settled = Boolean(s.payment_recorded_at || s.service_fee_locked);
  const livePct = b.service_fee_enabled ? (Number(b.service_fee_pct) || 0) : 0;
  const ownPct = s.service_fee_pct != null && Number.isFinite(Number(s.service_fee_pct)) ? Number(s.service_fee_pct) : null;
  // Settled bills keep the % locked at payment time.
  if (settled) return ownPct != null ? ownPct : 0;
  // Session 47 — OPEN bills always follow the live business setting when
  // this device knows it (a stale snapshot with pct 0/null must never hide
  // the fee); fall back to the % the server put on the session.
  if (b.service_fee_enabled != null) return livePct;
  return ownPct != null ? ownPct : 0;
}

/** Fee on an amount (after voucher) — mirrors backend compute_service_fee. */
export function serviceFeeOn(base, pct) {
  const b = Number(base) || 0;
  const p = Number(pct) || 0;
  if (b <= 0 || p <= 0) return 0;
  return Math.round(b * p) / 100;
}

/** Amount the guest still owes on a session snapshot (total − voucher + service fee). */
export function sessionAmountOwed(session, business) {
  const s = session || {};
  let total = Number(s.total_value) || 0;
  const hasOrders = Array.isArray(s.orders) && s.orders.length > 0;
  if (!total && hasOrders) {
    total = s.orders.reduce((sum, o) => sum + (o && o.status !== "voided" && !o.voided ? (Number(o.total ?? o.subtotal) || 0) : 0), 0);
  }
  // Session 62 — a server/snapshot `amount_owed` is only trusted when we
  // have nothing to recompute from; otherwise owed = live items − voucher
  // + (% × that base), the same rule the server applies.
  if (!total && !hasOrders && s.amount_owed != null && Number.isFinite(Number(s.amount_owed))) return _r2(s.amount_owed);
  const voucher = Number((s.applied_voucher || {}).discount_amount) || 0;
  const before = Math.max(0, total - voucher);
  const pct = sessionServiceFeePct(s, business);
  const storedFee = s.service_fee_amount != null && Number.isFinite(Number(s.service_fee_amount)) ? Number(s.service_fee_amount) : null;
  let fee;
  if (pct > 0) fee = serviceFeeOn(before, pct);
  else if (storedFee != null && s.service_fee_locked) fee = storedFee;
  else fee = 0;
  if (before <= 0) fee = 0;
  // Session 62 — the fee follows the money: items covered ⇒ settled, the
  // fee is what was collected on top (mirrors the server payload).
  const paid = _r2((Number(s.cash_paid) || 0) + (Number(s.card_paid) || 0) + (Number(s.eft_paid) || 0));
  if (paid > 0.009 && before > 0 && paid >= before - 0.009 && paid < _r2(before + fee) - 0.009) fee = _r2(Math.max(0, paid - before));
  return _r2(before + fee);
}

/**
 * Append `entries` to a session's ledger (skipping ids already present)
 * and return the session with every derived total recomputed. Pure —
 * does not touch IDB or React state.
 */
export function applyTendersToSession(session, entries, { recordedAt } = {}) {
  const s = session || {};
  const existing = Array.isArray(s.payments) ? s.payments.slice() : [];
  const seen = new Set(existing.map((p) => String((p && p.id) || "")));
  let cardN = existing.filter((p) => p && p.method === "card").length;
  for (const e of Array.isArray(entries) ? entries : []) {
    if (!e || !e.method) continue;
    const id = String(e.id || "");
    if (id && seen.has(id)) continue;
    if (id) seen.add(id);
    let label = e.label || null;
    if (!label) {
      if (e.method === "card") { cardN += 1; label = `Card ${cardN}`; }
      else label = e.method === "eft" ? "EFT" : "Cash";
    } else if (e.method === "card") {
      cardN += 1;
    }
    existing.push({
      id: id || `local-${Math.random().toString(36).slice(2, 10)}`,
      method: e.method,
      amount: _r2(e.amount),
      tip: _r2(e.tip),
      label,
      split_id: e.split_id || null,
      split_name: e.split_name || null,
      recorded_at: e.recorded_at || recordedAt || new Date().toISOString(),
      by: e.by || null,
      by_user_id: e.by_user_id || null,
    });
  }
  const t = ledgerTotals(existing);
  const owed = sessionAmountOwed(s);
  const settled = t.paid + 0.009 >= owed && owed > 0;
  const nowIso = recordedAt || new Date().toISOString();
  return {
    ...s,
    payments: existing,
    cash_paid: t.cash,
    card_paid: t.card,
    eft_paid: t.eft,
    tip_amount: t.tip,
    payment_method: methodFromTotals(t.cash, t.card, t.eft),
    paid_amount: t.paid,
    amount_owed: owed,
    outstanding_amount: _r2(Math.max(0, owed - t.paid)),
    payment_recorded_at: settled ? (s.payment_recorded_at || nowIso) : null,
    updated_at: nowIso,
  };
}

/**
 * Session 62 — recompute a session snapshot's bill figures from its LIVE
 * orders (voided orders are worth R0; line voids whose order total was
 * never recomputed fall back to the live lines). Returns a new session
 * with total_value / total_items / service_fee_amount / amount_owed /
 * outstanding_amount all consistent with the one server rule.
 */
export function withRecomputedBill(session, business) {
  const s = session || {};
  const orders = Array.isArray(s.orders) ? s.orders : [];
  const isVoided = (o) => !o || o.voided === true || String(o.status || "") === "voided";
  const liveRevenue = (o) => {
    const items = Array.isArray(o.items) ? o.items : [];
    const total = Number(o.total ?? o.subtotal) || 0;
    if (items.some((it) => it && it.voided)) {
      const liveSum = items.filter((it) => it && !it.voided).reduce((k, it) => k + (Number(it.line_total ?? ((Number(it.price || it.unit_price) || 0) * (Number(it.quantity) || 0))) || 0), 0);
      if (total > liveSum + 0.009) return liveSum;
    }
    return total;
  };
  const live = orders.filter((o) => !isVoided(o));
  const total_value = _r2(live.reduce((n, o) => n + liveRevenue(o), 0));
  const total_items = live.reduce((n, o) => n + (Array.isArray(o.items) ? o.items : []).filter((it) => it && !it.voided).reduce((k, it) => k + (Number(it.quantity) || 0), 0), 0);
  const next = { ...s, total_value, total_items };
  const voucher = Number((s.applied_voucher || {}).discount_amount) || 0;
  const before = Math.max(0, total_value - voucher);
  const pct = sessionServiceFeePct(next, business);
  if (pct > 0 || next.service_fee_amount != null) next.service_fee_amount = pct > 0 ? serviceFeeOn(before, pct) : (next.service_fee_locked ? Number(next.service_fee_amount) || 0 : 0);
  next.amount_before_service_fee = _r2(before);
  const owed = sessionAmountOwed(next, business);
  const paid = _r2((Number(s.cash_paid) || 0) + (Number(s.card_paid) || 0) + (Number(s.eft_paid) || 0));
  next.amount_owed = owed;
  next.paid_amount = paid;
  next.outstanding_amount = _r2(Math.max(0, owed - paid));
  // Session 72 — same definition as the server payload (Session 65).
  next.fully_paid = owed > 0 && paid >= owed - 0.009;
  return next;
}


// Session 31.26 — PAYMENT_ADDED reducer. Payload:
//   { table_session_id, payments: [{id, method, amount, tip, label}],
//     tip_recipient?, tip_recipient_user_id? }
// Appends to the local ledger (idempotent on tender id) and recomputes
// the derived totals the UI reads. Server-side `_apply_payment_added_event`
// does the same against Mongo and dedupes on change_id + tender id.
registerReducer(EventType.PAYMENT_ADDED, async (payload) => {
  if (!payload || typeof payload !== "object") return;
  const sid = payload.table_session_id;
  if (!sid || !Array.isArray(payload.payments) || !payload.payments.length) return;
  const session = await readOne("table_sessions", sid);
  const base = session && session.id ? session : { id: sid, status: "open", payments: [] };
  const next = applyTendersToSession(base, payload.payments, { recordedAt: payload.recorded_at });
  if (payload.tip_recipient) next.tip_recipient = payload.tip_recipient;
  if (payload.tip_recipient_user_id) next.tip_recipient_user_id = payload.tip_recipient_user_id;
  await putOne("table_sessions", next);
  try {
    const orders = await readAll("orders");
    const sessionOrders = orders.filter((o) => o && o.table_session_id === sid);
    for (const o of sessionOrders) {
      await putOne("orders", {
        ...o,
        cash_paid: next.cash_paid,
        card_paid: next.card_paid,
        eft_paid: next.eft_paid,
        payment_method: next.payment_method,
        updated_at: next.updated_at,
      });
    }
  } catch (_) { /* best effort */ }
});

// Session 30.26 — Diagnostics / automation hook. Lets the router-only
// LAN simulation (and support engineers in DevTools) place a write
// exactly as a dashboard would, without driving the UI. Read-only
// helpers are harmless to expose; `localWrite` requires a session
// object just like the dashboards pass.
if (typeof window !== "undefined") {
  try {
    window.__nfcsDebug = Object.assign(window.__nfcsDebug || {}, {
      localWrite,
      applyRemoteEvent,
      listQueuedEvents,
      EventType,
    });
  } catch (_) { /* noop */ }
}
